import React, { useCallback, useState, useRef } from 'react';
import { useDropzone } from 'react-dropzone';
import { Upload, Image as ImageIcon, X, AlertCircle, Crop, RotateCw } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from './button';

interface ImageUploadProps {
  onImageSelect: (file: File) => void;
  onImageRemove: () => void;
  selectedImage?: File | string | null;
  accept?: Record<string, string[]>;
  maxSize?: number;
  minDimensions?: { width: number; height: number };
  aspectRatio?: number;
  className?: string;
  disabled?: boolean;
  showPreview?: boolean;
  placeholder?: string;
}

const ImageUpload: React.FC<ImageUploadProps> = ({
  onImageSelect,
  onImageRemove,
  selectedImage,
  accept = {
    'image/png': ['.png'],
    'image/jpeg': ['.jpg', '.jpeg'],
    'image/svg+xml': ['.svg'],
    'image/webp': ['.webp']
  },
  maxSize = 5 * 1024 * 1024, // 5MB
  minDimensions = { width: 200, height: 200 },
  aspectRatio,
  className,
  disabled = false,
  showPreview = true,
  placeholder = "Upload an image"
}) => {
  const [error, setError] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const validateImageDimensions = useCallback((file: File): Promise<boolean> => {
    return new Promise((resolve) => {
      // Skip dimension validation for SVG files as they are vector-based
      if (file.type === 'image/svg+xml') {
        console.log('[IMAGE-UPLOAD] SVG file detected, skipping dimension validation');
        resolve(true);
        return;
      }

      const img = new Image();
      img.onload = () => {
        URL.revokeObjectURL(img.src);
        
        if (img.width < minDimensions.width || img.height < minDimensions.height) {
          setError(`Image must be at least ${minDimensions.width}x${minDimensions.height}px. Current: ${img.width}x${img.height}px`);
          resolve(false);
          return;
        }

        if (aspectRatio && Math.abs((img.width / img.height) - aspectRatio) > 0.1) {
          setError(`Image aspect ratio should be ${aspectRatio.toFixed(1)}:1`);
          resolve(false);
          return;
        }

        resolve(true);
      };
      img.onerror = () => {
        URL.revokeObjectURL(img.src);
        setError('Invalid image file');
        resolve(false);
      };
      img.src = URL.createObjectURL(file);
    });
  }, [minDimensions, aspectRatio]);

  const onDrop = useCallback(async (acceptedFiles: File[], rejectedFiles: any[]) => {
    setError(null);
    
    if (rejectedFiles.length > 0) {
      const rejection = rejectedFiles[0];
      if (rejection.errors.some((e: any) => e.code === 'file-too-large')) {
        setError(`File too large. Maximum size is ${Math.round(maxSize / 1024 / 1024)}MB`);
      } else if (rejection.errors.some((e: any) => e.code === 'file-invalid-type')) {
        setError('Please upload a valid image file (PNG, JPG, SVG, or WebP)');
      } else {
        setError('File upload failed. Please try again.');
      }
      return;
    }

    if (acceptedFiles.length > 0) {
      const file = acceptedFiles[0];
      
      // Validate image dimensions
      const isValid = await validateImageDimensions(file);
      if (!isValid) return;

      // Create preview URL
      const url = URL.createObjectURL(file);
      setPreviewUrl(url);
      
      onImageSelect(file);
    }
  }, [onImageSelect, maxSize, validateImageDimensions]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept,
    maxSize,
    multiple: false,
    disabled,
    onDragEnter: () => setIsDragOver(true),
    onDragLeave: () => setIsDragOver(false),
  });

  const handleRemove = useCallback(() => {
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
    }
    setError(null);
    onImageRemove();
  }, [previewUrl, onImageRemove]);

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getImageUrl = () => {
    if (previewUrl) return previewUrl;
    if (typeof selectedImage === 'string') return selectedImage;
    return null;
  };

  const imageUrl = getImageUrl();

  return (
    <div className={cn("space-y-2", className)}>
      {!selectedImage ? (
        <div
          {...getRootProps()}
          className={cn(
            "border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-all duration-200",
            isDragActive || isDragOver
              ? "border-primary bg-primary/5 scale-[1.02]" 
              : "border-muted-foreground/25 hover:border-primary/50 hover:bg-muted/25",
            disabled && "opacity-50 cursor-not-allowed"
          )}
        >
          <input {...getInputProps()} />
          <div className="flex flex-col items-center gap-3">
            <div className="p-3 rounded-full bg-muted">
              <Upload className="w-6 h-6 text-muted-foreground" />
            </div>
            <div>
              <p className="text-sm font-medium">
                {isDragActive ? "Drop the image here" : placeholder}
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                PNG, JPG, SVG, or WebP up to {Math.round(maxSize / 1024 / 1024)}MB
              </p>
              <p className="text-xs text-muted-foreground">
                Minimum: {minDimensions.width}x{minDimensions.height}px
              </p>
            </div>
            <Button 
              type="button" 
              variant="outline" 
              size="sm"
              className="mt-2 pointer-events-none"
              disabled={disabled}
            >
              <ImageIcon className="w-4 h-4 mr-2" />
              Choose Image
            </Button>
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          {showPreview && imageUrl && (
            <div className="relative group">
              <div className="border rounded-lg overflow-hidden bg-muted/25">
                <img
                  src={imageUrl}
                  alt="Preview"
                  className="w-full h-48 object-contain"
                />
              </div>
              <Button
                type="button"
                variant="destructive"
                size="sm"
                className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity"
                onClick={handleRemove}
                disabled={disabled}
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          )}
          
          <div className="flex items-center justify-between p-3 border rounded-lg bg-muted/25">
            <div className="flex items-center gap-3">
              <ImageIcon className="w-5 h-5 text-primary" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">
                  {selectedImage instanceof File 
                    ? selectedImage.name 
                    : "Current image"
                  }
                </p>
                {selectedImage instanceof File && (
                  <p className="text-xs text-muted-foreground">
                    {formatFileSize(selectedImage.size)}
                  </p>
                )}
              </div>
            </div>
            
            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => fileInputRef.current?.click()}
                disabled={disabled}
              >
                <RotateCw className="w-4 h-4 mr-2" />
                Replace
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={handleRemove}
                disabled={disabled}
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          </div>

          <input
            {...getInputProps()}
            ref={fileInputRef}
            style={{ display: 'none' }}
          />
        </div>
      )}
      
      {error && (
        <div className="flex items-center gap-2 text-sm text-destructive bg-destructive/10 p-3 rounded-lg border border-destructive/20">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          {error}
        </div>
      )}
    </div>
  );
};

export default ImageUpload;